import React, {Fragment, useEffect, useMemo, useState} from "react";
import {
    Button,
    Card,
    CardBody,
    CardFooter,
    CardHeader,
    Col,
    Container,
    Form,
    Input,
    Modal,
    ModalBody,
    ModalHeader,
    Row,
    Table,
} from "reactstrap";
import SimpleHeader from "components/Headers/SimpleHeader.js";
import PaginationWrapper from "../components/pagination";
import {numberWithCommas, telWithCommas} from "../../../utils";
import useHistoryState from "../../../common/useHistoryState";
import {useHistory} from "react-router";
import ReactDatetime from "react-datetime";
import AuthService from "../auth/auth.service";
import sharingPloggingService from "./service/sharingPlogging.service";
import dayjs from "dayjs";

const INITIAL_SUMMARY = {
    participantCount: 0,
    activityCount: 0,
    donationAmount: 0,
};

const formatDateTime = (value) => {
    if (!value) {
        return "-";
    }
    return dayjs(value).isValid() ? dayjs(value).format("YYYY.MM.DD HH:mm") : value;
};

const formatUsageTime = (value) => {
    if (!value) {
        return "0시간";
    }
    const [hours = "0", minutes = "0"] = String(value).split(":");
    if (Number(minutes) > 0) {
        return `${Number(hours) || 0}시간 ${Number(minutes)}분`;
    }
    return `${Number(hours) || 0}시간`;
};

function SharedPloggingCalcMgmt({match}) {
    const user = typeof window !== 'undefined' ? AuthService.getCurrentUser() : null;
    const history = useHistory();
    const {centerId: routeCenterId} = match.params || {};
    const isCenterUser = user?.role === "center";

    const [pageActive, setPageActive] = useHistoryState(1, 'pageActive');
    const [pageCount, setPageCount] = useHistoryState(1, 'pageCount');
    const [adminData, setAdminData] = useHistoryState([], 'data');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const [selectedMonthInput, setSelectedMonthInput] = useState(dayjs().format("YYYY-MM"));
    const [selectedMonth, setSelectedMonth] = useState(dayjs().format("YYYY-MM"));
    const [centerRows, setCenterRows] = useState([]);
    const [centerSummary, setCenterSummary] = useState(INITIAL_SUMMARY);
    const [centerName, setCenterName] = useState("");
    const [detailOpen, setDetailOpen] = useState(false);
    const [selectedParticipant, setSelectedParticipant] = useState(null);

    const resolvedCenterId = useMemo(() => {
        if (routeCenterId && routeCenterId !== ':centerId') {
            return Number(routeCenterId);
        }
        return user?.centerId ? Number(user.centerId) : undefined;
    }, [routeCenterId, user?.centerId]);

    const fetchAdminData = () => {
        const revisedStartDate = !startDate ? undefined : dayjs(startDate).startOf('month').format('YYYY-MM-DD');
        const revisedEndDate = !startDate ? undefined : dayjs(startDate).endOf('month').format('YYYY-MM-DD');
        const option = {
            startDate: revisedStartDate,
            endDate: revisedEndDate,
        };

        option.page = pageActive;
        if (resolvedCenterId) {
            option.centerId = resolvedCenterId;
        }

        sharingPloggingService.list(option)
            .then((res) => {
                if (res.code === 200) {
                    setPageCount(res.list.count);
                    setAdminData(res.list.rows);
                }
            });
    };

    const fetchCenterSettlement = () => {
        if (!resolvedCenterId) {
            setCenterRows([]);
            setCenterSummary(INITIAL_SUMMARY);
            setCenterName("");
            setPageCount(1);
            return;
        }

        const monthDate = dayjs(`${selectedMonth}-01`);
        const option = {
            view: "centerSettlement",
            centerId: resolvedCenterId,
            page: pageActive,
            startDate: monthDate.startOf("month").format("YYYY-MM-DD"),
            endDate: monthDate.endOf("month").format("YYYY-MM-DD"),
        };

        sharingPloggingService.list(option)
            .then((res) => {
                if (res.code === 200) {
                    setCenterRows(Array.isArray(res.list?.rows) ? res.list.rows : []);
                    setCenterSummary(res.list?.summary || INITIAL_SUMMARY);
                    setCenterName(res.list?.centerName || "");
                    setPageCount(res.list?.count || 1);
                }
            });
    };

    useEffect(() => {
        if (isCenterUser) {
            fetchCenterSettlement();
            return;
        }
        fetchAdminData();
    }, [isCenterUser, pageActive, selectedMonth, startDate, endDate, resolvedCenterId]);

    const handleReactDatetimeChange = (who, date) => {
        if (
            startDate &&
            who === "endDate" &&
            new Date(startDate) > new Date(date.format('YYYY-MM-DD'))
        ) {
            setStartDate(date.format('YYYY-MM-DD'));
            setEndDate(date.format('YYYY-MM-DD'));
        } else if (
            endDate &&
            who === "startDate" &&
            new Date(endDate) < new Date(date.format('YYYY-MM-DD'))
        ) {
            setStartDate(date.format('YYYY-MM-DD'));
            setEndDate(date.format('YYYY-MM-DD'));
        } else {
            if (who === "startDate") {
                setStartDate(date.format('YYYY-MM-DD'));
            } else {
                setEndDate(date.format('YYYY-MM-DD'));
            }
        }
    };

    const openParticipantDetail = (item) => {
        setSelectedParticipant(item);
        setDetailOpen(true);
    };

    const usageDetails = (selectedParticipant?.details || []).filter((item) => item.type === "usage");

    const renderCenterView = () => {
        return (
            <>
                <SimpleHeader name="Tables" parentName="Tables"/>
                <Container className="mt--6" fluid>
                    <Row>
                        <div className="col">
                            <Card>
                                <CardHeader className="border-0">
                                    <Row className="align-items-end">
                                        <Col lg="4" md="6">
                                            <label className="form-control-label">조회 월</label>
                                            <Input
                                                type="month"
                                                value={selectedMonthInput}
                                                onChange={(e) => setSelectedMonthInput(e.target.value || dayjs().format("YYYY-MM"))}
                                            />
                                        </Col>
                                        <Col lg="4" md="6" className="mt-3 mt-md-0">
                                            <Button
                                                className="btn-default mr-2"
                                                onClick={() => {
                                                    setSelectedMonth(selectedMonthInput || dayjs().format("YYYY-MM"));
                                                    setPageActive(1);
                                                }}
                                            >
                                                검색
                                            </Button>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <CardBody className="pt-0">
                                    <Row>
                                        <Col lg="4" md="6" className="mt-3">
                                            <Card className="mb-0 shadow-none border">
                                                <CardBody>
                                                    <div className="text-muted small">기간 내 참여 인원 수</div>
                                                    <div className="h2 mb-0 mt-2">{numberWithCommas(centerSummary.participantCount || 0)}명</div>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                        <Col lg="4" md="6" className="mt-3">
                                            <Card className="mb-0 shadow-none border">
                                                <CardBody>
                                                    <div className="text-muted small">기간 내 활동 수</div>
                                                    <div className="h2 mb-0 mt-2">{numberWithCommas(centerSummary.activityCount || 0)}건</div>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                        <Col lg="4" md="12" className="mt-3">
                                            <Card className="mb-0 shadow-none border">
                                                <CardBody>
                                                    <div className="text-muted small">기간 내 기부 금액</div>
                                                    <div className="h2 mb-0 mt-2">{numberWithCommas(centerSummary.donationAmount || 0)}원</div>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                    </Row>
                                </CardBody>
                                <Table className="align-items-center table-flush" responsive striped>
                                    <thead className="thead-light">
                                    <tr>
                                        <th className="text-center">이름</th>
                                        <th className="text-center">아이디</th>
                                        <th className="text-center">전화번호</th>
                                        <th className="text-center">기부 금액</th>
                                        <th className="text-center">플로깅 수</th>
                                        <th className="text-center">플로깅 봉사시간</th>
                                        <th className="text-center">상세</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {centerRows.length === 0 ? (
                                        <tr>
                                            <td className="text-center" colSpan="7">해당 기간에 결제한 사람이 없습니다.</td>
                                        </tr>
                                    ) : centerRows.map((item) => (
                                        <tr key={item.id}>
                                            <td className="text-center">{item.name || '-'}</td>
                                            <td className="text-center">{item.loginId || '-'}</td>
                                            <td className="text-center">{telWithCommas(item.tel || '-')}</td>
                                            <td className="text-center">{numberWithCommas(item.donationAmount || 0)}원</td>
                                            <td className="text-center">{numberWithCommas(item.ploggingCount || 0)}건</td>
                                            <td className="text-center">{formatUsageTime(item.totalUsageTime)}</td>
                                            <td className="text-center">
                                                <Button
                                                    className="btn-outline-warning"
                                                    size="sm"
                                                    onClick={() => openParticipantDetail(item)}
                                                >
                                                    상세
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </Table>
                                <CardFooter className="py-4">
                                    <PaginationWrapper
                                        isActive={pageActive}
                                        pageNumber={pageCount}
                                        callback={(page) => setPageActive(page)}
                                        itemPerPage={10}
                                    />
                                </CardFooter>
                            </Card>
                        </div>
                    </Row>
                </Container>

                <Modal isOpen={detailOpen} toggle={() => setDetailOpen(false)} size="lg">
                    <ModalHeader toggle={() => setDetailOpen(false)}>
                        {selectedParticipant?.name || '-'} 상세
                    </ModalHeader>
                    <ModalBody>
                        <Row className="mb-3">
                            <Col md="4"><strong>아이디</strong><div>{selectedParticipant?.loginId || '-'}</div></Col>
                            <Col md="4"><strong>전화번호</strong><div>{telWithCommas(selectedParticipant?.tel || '-')}</div></Col>
                            <Col md="4"><strong>총 기부 금액</strong><div>{numberWithCommas(selectedParticipant?.donationAmount || 0)}원</div></Col>
                        </Row>
                        <Table className="align-items-center table-flush" responsive striped>
                            <thead className="thead-light">
                            <tr>
                                <th className="text-center">이용일시</th>
                                <th className="text-center">이용시간</th>
                                <th className="text-center">거리</th>
                                <th className="text-center">주소</th>
                                <th className="text-center">상세</th>
                            </tr>
                            </thead>
                            <tbody>
                            {usageDetails.length === 0 ? (
                                <tr>
                                    <td className="text-center" colSpan="5">해당 월 이용 내역이 없습니다.</td>
                                </tr>
                            ) : usageDetails.map((detail, index) => (
                                <tr key={`${detail.ploggingId || detail.sharingPloggingId || 'usage'}-${index}`}>
                                    <td className="text-center">{formatDateTime(detail.usedAt)}</td>
                                    <td className="text-center">{formatUsageTime(detail.time)}</td>
                                    <td className="text-center">{detail.distance ? `${detail.distance}km` : '-'}</td>
                                    <td className="text-center">{detail.address || '-'}</td>
                                    <td className="text-center">
                                        {detail.userCenterId ? (
                                            <Button
                                                className="btn-outline-default"
                                                size="sm"
                                                onClick={() => history.push(`/admin/sharedPloggingCalcMgmtPloggingDetail/${detail.sharingPloggingId}/${encodeURIComponent(centerName || "")}/${detail.userCenterId}`)}
                                            >
                                                상세
                                            </Button>
                                        ) : '-'}
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </Table>
                    </ModalBody>
                </Modal>
            </>
        );
    };

    const renderAdminView = () => {
        return (
            <>
                <SimpleHeader name="Tables" parentName="Tables"/>
                <Container className="mt--6" fluid>
                    <Row>
                        <div className="col">
                            <Card>
                                <CardHeader className="border-0">
                                    <Row className="py-3">
                                        <Col xs="11">
                                            <Form>
                                                <Row>
                                                    <Col md="auto">
                                                        <label className="form-control-label">날짜 조회</label>
                                                        <Row style={{gap: 16}}>
                                                            <ReactDatetime
                                                                inputProps={{placeholder: ""}}
                                                                value={startDate}
                                                                locale={'ko'}
                                                                timeFormat={false}
                                                                dateFormat={'YYYY-MM'}
                                                                onChange={(e) => handleReactDatetimeChange("startDate", e)}
                                                            />
                                                            <Button
                                                                className="btn-default"
                                                                onClick={() => {
                                                                    setStartDate('');
                                                                    setEndDate('');
                                                                }}
                                                            >
                                                                <span className="btn-inner-text">초기화</span>
                                                            </Button>
                                                        </Row>
                                                    </Col>
                                                </Row>
                                            </Form>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col xs="6">
                                            <h3 className="mt-2">나눔플로깅 리스트 및 정산 관리</h3>
                                        </Col>
                                    </Row>
                                </CardHeader>

                                <Table className="align-items-center table-flush" responsive striped>
                                    <thead className="thead-light">
                                    <tr>
                                        <th className="text-center" style={{width: '10%'}}>봉사 센터명</th>
                                        <th className="text-center" style={{width: '10%'}}>참여 인원 수</th>
                                        <th className="text-center" style={{width: '10%'}}>건별 활동 수</th>
                                        <th className="text-center" style={{width: '10%'}}>결제 금액</th>
                                        <th className="text-center" style={{width: '10%'}}>기부 전달 금액</th>
                                        <th className="text-center" style={{width: '45%'}}></th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {adminData?.map((item, i) => (
                                        <Fragment key={i}>
                                            <tr>
                                                <td className="text-center">{item.center}</td>
                                                <td className="text-center">{numberWithCommas(item.totalCount)}</td>
                                                <td className="text-center">{numberWithCommas(item.workingCount)}</td>
                                                <td className="text-center" style={{display: 'flex', gap: 8, justifyContent: 'center', alignItems: 'center'}}>
                                                    {numberWithCommas(item.payAmount)}
                                                    <Button
                                                        className="btn-outline-warning"
                                                        onClick={() => history.push('/admin/sharedPloggingCalcMgmtPlogging/' + item.id + `/${item.center}`)}
                                                        size="sm"
                                                    >
                                                        <span className="btn-inner--text">리스트 보기</span>
                                                    </Button>
                                                </td>
                                                <td className="text-center">{numberWithCommas(item.totalPrice)}</td>
                                            </tr>
                                        </Fragment>
                                    ))}
                                    </tbody>
                                </Table>

                                <CardFooter className="py-4">
                                    <PaginationWrapper
                                        isActive={pageActive}
                                        pageNumber={pageCount}
                                        callback={(page) => setPageActive(page)}
                                        itemPerPage={10}
                                    />
                                </CardFooter>
                            </Card>
                        </div>
                    </Row>
                </Container>
            </>
        );
    };

    return isCenterUser ? renderCenterView() : renderAdminView();
}

export default SharedPloggingCalcMgmt;
